import { useMutation, useQuery } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RefreshCw, Mail, CalendarClock } from "lucide-react";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import type { RecurringBillingQueueItem } from "@shared/contracts";

interface ProcessResult {
  processed: number;
}

export default function RenewalsPage() {
  const { toast } = useToast();

  const { data: renewals, isLoading } = useQuery<RecurringBillingQueueItem[]>({
    queryKey: ["/api/billing/renewals"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const processRemindersMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        "/api/billing/renewals/process-reminders",
      );
      return (await res.json()) as ProcessResult;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/renewals"] });
      toast({
        title: "Reminders processed",
        description: `${data.processed} reminder(s) sent.`,
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to process reminders",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const processRenewalsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        "/api/billing/renewals/process-renewals",
      );
      return (await res.json()) as ProcessResult;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/renewals"] });
      toast({
        title: "Renewals processed",
        description: `${data.processed} renewal(s) processed.`,
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to process renewals",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const columns: ColumnDef<RecurringBillingQueueItem>[] = [
    {
      key: "service",
      header: "Service",
      cell: (row) => (
        <div className="min-w-0">
          <div className="font-medium text-sm">{row.serviceName}</div>
          <div className="text-xs text-muted-foreground">{row.clientName}</div>
        </div>
      ),
    },
    {
      key: "product",
      header: "Product",
      cell: (row) => <span className="text-sm">{row.productName}</span>,
    },
    {
      key: "amount",
      header: "Amount",
      cell: (row) => formatCurrency(row.amount, row.currency),
    },
    {
      key: "dueAt",
      header: "Due",
      cell: (row) => formatDate(row.dueAt),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => <StatusBadge type="renewal" status={row.status} />,
    },
    {
      key: "lastReminder",
      header: "Last Reminder",
      cell: (row) =>
        row.lastReminderAt ? formatDateTime(row.lastReminderAt) : "\u2014",
    },
  ];

  const queueCounts = {
    pending: renewals?.filter((row) => row.status === "pending").length ?? 0,
    emailed: renewals?.filter((row) => row.status === "emailed").length ?? 0,
    failed: renewals?.filter((row) => row.status === "failed").length ?? 0,
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Renewals"
        description="View and process service renewals"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => processRemindersMutation.mutate()}
              disabled={processRemindersMutation.isPending}
            >
              {processRemindersMutation.isPending ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Mail className="mr-2 h-4 w-4" />
              )}
              Process Reminders
            </Button>
            <Button
              onClick={() => processRenewalsMutation.mutate()}
              disabled={processRenewalsMutation.isPending}
            >
              {processRenewalsMutation.isPending ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Process Renewals
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <QueueCard label="Ready to Send" value={queueCounts.pending} />
        <QueueCard label="Awaiting Client Action" value={queueCounts.emailed} />
        <QueueCard label="Needs Attention" value={queueCounts.failed} />
      </div>

      <DataTable
        columns={columns}
        data={renewals}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        emptyIcon={CalendarClock}
        emptyTitle="No renewals"
        emptyDescription="There are no renewals to display yet."
      />
    </div>
  );
}

function QueueCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}
